import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Modal, Select, Tree, Typography } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Icon } from '@/components';
import { message } from '@/components/Message';
import './index.less';

export interface OrganizationTreeTogetherBO {
  /** 部门/员工id */
  id: string;
  /* 部门名称/员工姓名 */
  name: string;
  /** 部门/员工标识：1-部门，2-员工 */
  organizeFlag: number;
  /* 父级部门id */
  parentId: string;
  /* 员工状态：1-启用，2-禁用，3-待激活，4-待加入，5-已离职 */
  status: number;
  /* 子节点标识：0-无子节点，1-有子节点 */
  subFlag: number;
  /* 子节点列表 */
  tree?: OrganizationTreeTogetherBO[];
  /* 企业微信userid */
  wechatUserid: boolean;
  [property: string]: any;
}

interface TreeNode {
  _key?: string;
  title: string;
  key: string;
  children?: TreeNode[];
  icon?: React.ReactElement;
}

interface TreeSelectProps {
  value: string[];
  onChange?: (value: string[]) => void;
  dataSource: OrganizationTreeTogetherBO[];
}

function formatEmployeeTree(tree: OrganizationTreeTogetherBO[]) {
  const parentKeys = new Set<string>();

  const flats: TreeNode[] = [];

  const employeeMap = new Map<string, TreeNode>();

  const dep = (node: OrganizationTreeTogetherBO, parentKey?: string) => {
    const isEmployee = +node.organizeFlag === 2;

    if (!isEmployee && !node.tree?.length) {
      return;
    }

    const _key = parentKey ? `${parentKey}-${node.id}` : node.id;

    const treeNode: TreeNode = {
      _key,
      title: node.name,
      key: node.id,
      children: isEmployee
        ? undefined
        : node.tree?.map((item) => dep(item, _key)).filter((item) => !!item),
      icon: <Icon type={isEmployee ? 'icon-yuangong' : 'icon-bumen'} />,
    };

    if (!isEmployee && !treeNode.children?.length) {
      return;
    }

    flats.push(treeNode);

    if (isEmployee) {
      employeeMap.set(node.id, treeNode);
      parentKey?.split('-').forEach((key) => parentKeys.add(key));
    }

    return treeNode;
  };

  const result = tree.map((node) => dep(node)).filter((item) => !!item);

  return { flats, employeeMap, parentKeys, tree: result };
}

export default function TreeSelect({ value, onChange, dataSource }: TreeSelectProps) {
  const compositionRef = useRef(false);

  const [open, setOpen] = useState(false);

  const [filterText, setFilterText] = useState('');

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const { flats, employeeMap, parentKeys, tree } = useMemo(() => {
    const res = formatEmployeeTree(dataSource);
    setCheckedKeys([...res.employeeMap.keys()]);
    return res;
  }, [dataSource]);

  const selectOptions = useMemo(() => {
    return [...employeeMap.values()].map((item) => ({ label: item.title, value: item.key }));
  }, [employeeMap]);

  const handleFilter = (text: string) => {
    const filters = flats
      .filter((item) => item.title.indexOf(text) > -1)
      .reduce<string[]>((res, item) => {
        const keys = item._key?.split('-') || [];

        for (let i = 0; i < keys.length - 1; i++) {
          res.push(keys[i]);
        }
        return res;
      }, []);
    setExpandedKeys([...new Set(filters)]);
  };

  const handleExpand = (keys: string[]) => {
    const expands = keys.reduce<string[]>((res, key) => {
      const item = employeeMap.get(key);
      const keys = item?._key?.split('-') || [];

      for (let i = 0; i < keys.length - 1; i++) {
        res.push(keys[i]);
      }

      return res;
    }, []);
    setExpandedKeys(expands);
  };

  useEffect(() => {
    if (employeeMap.size) {
      onChange?.([...employeeMap.keys()]);
    }
  }, [employeeMap]);

  return (
    <>
      <Select
        style={{ width: 300 }}
        open={false}
        mode="multiple"
        value={value}
        removeIcon={null}
        options={selectOptions}
        placeholder="选择员工"
        onClick={() => {
          setExpandedKeys([]);
          setCheckedKeys([...value]);
          handleExpand(value);
          setOpen(true);
        }}
        maxTagCount={2}
      />
      <Modal
        wrapClassName="employee-select-modal"
        open={open}
        title="选择员工"
        width={600}
        centered
        onOk={() => {
          if (!checkedKeys.length) {
            message.info('请选择员工');
            return;
          }
          onChange?.([...checkedKeys]);
          setOpen(false);
        }}
        onCancel={() => setOpen(false)}
      >
        <div className="left">
          <Input
            value={filterText}
            prefix={<SearchOutlined />}
            placeholder="请输入部门或员工"
            onChange={(e) => {
              const text = e.target.value.trim();
              setFilterText(text);
              if (text && !compositionRef.current) {
                handleFilter(text);
              }
            }}
            onCompositionStart={() => {
              compositionRef.current = true;
            }}
            onCompositionEnd={() => {
              compositionRef.current = false;
              handleFilter(filterText);
            }}
          />
          <Tree
            checkable
            treeData={tree}
            multiple
            showLine
            height={400}
            showIcon
            checkedKeys={checkedKeys}
            expandedKeys={expandedKeys}
            onCheck={(checkd) => {
              const employeeIds = (checkd as string[]).filter((key) => !parentKeys.has(key));
              setCheckedKeys([...employeeIds]);
            }}
            filterTreeNode={(node) => (filterText ? node.title.includes(filterText) : false)}
            onExpand={(expandedKeys) => setExpandedKeys(expandedKeys as string[])}
          />
        </div>
        <div className="right">
          <div className="title">已选择的员工({checkedKeys.length})</div>
          <div className="list">
            {checkedKeys.map((id, idx) => (
              <div className="item" key={id}>
                <Icon type="icon-yuangong" />
                <Typography.Text
                  className="name"
                  ellipsis={{ tooltip: employeeMap.get(id)?.title }}
                >
                  {employeeMap.get(id)?.title}
                </Typography.Text>
                <CloseOutlined
                  className="remove"
                  onClick={() => {
                    setCheckedKeys((list) => {
                      list.splice(idx, 1);
                      return [...list];
                    });
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}
